import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, BookmarkPlus, ArrowRight, Bookmark } from "lucide-react";

const CHART_ICONS = {
  "amino-acids": "🧬",
  "neurotransmitters": "🧠",
  "functional-groups": "⚗️",
  "enzyme-kinetics": "📈",
};

export default function Decks() {
  const [decks, setDecks] = useState(null);
  useEffect(() => {
    api.get("/custom-flashcards")
      .then((r) => setDecks(r.data.decks || []))
      .catch(() => setDecks([]));
  }, []);

  if (decks === null) {
    return (
      <div className="text-sm py-10 text-center" style={{ color: "var(--text-muted)" }}>
        <Loader2 className="mx-auto animate-spin mb-2" size={20} />
        Loading your decks…
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="decks-page">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--primary)" }}>
          <Bookmark size={18} color="white" />
        </div>
        <h1 className="font-display text-4xl font-bold">My Decks</h1>
      </div>
      <p className="text-lg mb-8" style={{ color: "var(--text-secondary)" }}>
        Reference charts you've saved as flashcard decks. Drill anytime, from any device.
      </p>

      {decks.length === 0 ? (
        <div className="pm-card p-10 text-center" data-testid="decks-empty">
          <BookmarkPlus size={32} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
          <p className="text-lg font-display font-bold mb-1">No decks saved yet</p>
          <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>
            Open a topic that has a reference chart (Amino Acids, Neurotransmitters, Enzyme Kinetics, Functional Groups) and tap <b>Save as flashcards</b>.
          </p>
          <Link to="/app/subjects" className="pm-btn-primary inline-flex">Browse subjects</Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="decks-grid">
          {decks.map((d) => {
            const acc = d.accuracy;
            const badgeColor = acc == null ? "var(--muted)"
              : acc >= 0.8 ? "#10b98122"
              : acc >= 0.5 ? "#f59e0b22"
              : "#ef444422";
            const badgeText = acc == null ? "var(--text-muted)"
              : acc >= 0.8 ? "#10b981"
              : acc >= 0.5 ? "#d97706"
              : "#ef4444";
            return (
              <Link
                key={d.id}
                to={`/app/decks/${d.id}`}
                className="pm-card pm-card-hover p-5"
                data-testid={`deck-tile-${d.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="text-3xl">{CHART_ICONS[d.source_chart] || "📚"}</div>
                  {acc != null && (
                    <span
                      className="text-xs font-bold px-2 py-1 rounded-full"
                      style={{ background: badgeColor, color: badgeText }}
                    >
                      {Math.round(acc * 100)}%
                    </span>
                  )}
                </div>
                <div className="font-display font-bold text-lg mb-1 line-clamp-1">{d.title}</div>
                <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>
                  {d.card_count} cards · {d.attempts || 0} drills
                </div>
                <div className="text-sm inline-flex items-center gap-1" style={{ color: "var(--primary)" }}>
                  Drill this deck <ArrowRight size={12} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
